import {
    RequestService,
    RequestServiceResponse,
} from '@tcc-online/request-service';
import AdminTeam from '../types/team/AdminTeam';
import IPagination from '../../types/IPagination';
import AdminTeamParticipant from '../types/team/AdminTeamParticipant';
import { Report, ReportResult } from '@tcc-online/output-report';

const BASE_URL = '/admin/teams';

const createProjectTeamsUrl = (
    type: number,
    projectId: number,
    teamId?: number,
) =>
    `/admin/products/${type}/projects/${projectId}/teams${teamId != null ? `/${teamId}` : ''}`;

const AdminTeamService = {
    getTeams,
    getTeam,
    createTeam,
    updateTeam,
    deleteTeam,
    copyTeamToProject,
    addTeamParticipant,
    getTeamParticipants,
    getTeamParticipant,
    updateTeamParticipant,
    deleteTeamParticipant,
    getProjectTeams,
    getProjectTeam,
    createProjectTeam,
    updateProjectTeam,
    deleteProjectTeam,
    copyProjectTeamToCompany,
    addProjectTeamParticipant,
    getProjectTeamParticipants,
    getProjectTeamParticipant,
    updateProjectTeamParticipant,
    deleteProjectTeamParticipant,
    getTeamReports,
    getTeamReportsByType,
    getTeamReport,
    getTeamReportResult,
    getProjectTeamReports,
    getProjectTeamReport,
    getProjectTeamReportResult,
};

export interface GetTeamsPayload {
    page?: number;
    limit?: number;
    search?: string;
}

export interface CreateTeamPayload {
    name?: string;
}

export interface UpdateTeamPayload {
    teamId: number;
    name?: string;
    participants?: AdminTeamParticipant[];
}

export interface CopyTeamToProjectPayload {
    teamId: number;
    projectId: number;
}

export interface ProjectTeamBasePayload {
    type: number;
    projectId: number;
}

export interface GetProjectTeamsPayload extends ProjectTeamBasePayload {
    page?: number;
    limit?: number;
}

export interface CreateProjectTeamPayload extends ProjectTeamBasePayload {
    name: string;
}

export interface UpdateProjectTeamPayload extends ProjectTeamBasePayload {
    teamId: number;
    name?: string;
    participants?: AdminTeamParticipant[];
}

export interface CopyProjectTeamToCompanyPayload extends ProjectTeamBasePayload {
    teamId: number;
}

export interface AddTeamParticipantPayload {
    teamId: number;
    participantId: number;
    color?: string;
    isManager?: boolean;
}

export interface AddProjectTeamParticipantPayload extends ProjectTeamBasePayload {
    teamId: number;
    participantId: number;
    color?: string;
    isManager?: boolean;
}

export interface GetTeamParticipantsPayload {
    teamId: number;
}

export interface GetTeamParticipantPayload {
    teamId: number;
    participantId: number;
}

export interface UpdateTeamParticipantPayload {
    teamId: number;
    participantId: number;
    color?: string;
    isManager?: boolean;
}

export interface DeleteTeamParticipantPayload {
    teamId: number;
    participantId: number;
}

export interface GetProjectTeamParticipantsPayload extends ProjectTeamBasePayload {
    teamId: number;
}

export interface GetProjectTeamParticipantPayload extends ProjectTeamBasePayload {
    teamId: number;
    participantId: number;
}

export interface UpdateProjectTeamParticipantPayload extends ProjectTeamBasePayload {
    teamId: number;
    participantId: number;
    color?: string;
    isManager?: boolean;
}

export interface DeleteProjectTeamParticipantPayload extends ProjectTeamBasePayload {
    teamId: number;
    participantId: number;
}

export interface GetTeamReportsPayload {
    teamId: number;
    page?: number;
    limit?: number;
}

export interface GetTeamReportsByTypePayload {
    teamId: number;
    reportType: number;
    page?: number;
    limit?: number;
}

export interface GetTeamReportPayload {
    teamId: number;
    reportId: number;
}

export interface GetTeamReportResultPayload {
    teamId: number;
    reportId: number;
    resultId: number;
}

export interface GetProjectTeamReportsPayload extends ProjectTeamBasePayload {
    teamId: number;
    page?: number;
    limit?: number;
}

export interface GetProjectTeamReportPayload extends ProjectTeamBasePayload {
    teamId: number;
    reportId: number;
}

export interface GetProjectTeamReportResultPayload extends ProjectTeamBasePayload {
    teamId: number;
    reportId: number;
    resultId: number;
}

async function getTeams(
    payload: GetTeamsPayload = {},
): Promise<RequestServiceResponse<AdminTeam[]> & { pagination?: IPagination }> {
    const { page = 1, limit = 10, search } = payload;
    return await RequestService.get<AdminTeam[]>(BASE_URL, {
        limit,
        page,
        search,
    });
}

async function getTeam(
    teamId: number,
): Promise<RequestServiceResponse<AdminTeam>> {
    return await RequestService.get<AdminTeam>(`${BASE_URL}/${teamId}`);
}

async function createTeam(
    payload: CreateTeamPayload,
): Promise<RequestServiceResponse<AdminTeam>> {
    const { name } = payload;
    return await RequestService.post<CreateTeamPayload, AdminTeam>(BASE_URL, {
        name,
    });
}

async function updateTeam(
    payload: UpdateTeamPayload,
): Promise<RequestServiceResponse<AdminTeam>> {
    const { teamId, name, participants } = payload;
    const body: Omit<UpdateTeamPayload, 'teamId'> = {};
    if (name !== undefined) {
        body.name = name;
    }
    if (participants !== undefined) {
        body.participants = participants;
    }
    return await RequestService.patch<
        Omit<UpdateTeamPayload, 'teamId'>,
        AdminTeam
    >(`${BASE_URL}/${teamId}`, body, { allowMultiRequest: true });
}

async function deleteTeam(teamId: number): Promise<RequestServiceResponse> {
    return await RequestService.delete(`${BASE_URL}/${teamId}`);
}

async function copyTeamToProject(
    payload: CopyTeamToProjectPayload,
): Promise<RequestServiceResponse<unknown>> {
    const { teamId, projectId } = payload;
    return await RequestService.post<{ projectId: number }, unknown>(
        `${BASE_URL}/${teamId}/copy-to-project`,
        { projectId },
    );
}

async function getProjectTeams(
    payload: GetProjectTeamsPayload,
): Promise<RequestServiceResponse<AdminTeam[]> & { pagination?: IPagination }> {
    const { type, projectId, page = 1, limit = 10 } = payload;
    return await RequestService.get<AdminTeam[]>(
        createProjectTeamsUrl(type, projectId),
        { limit, page },
    );
}

async function getProjectTeam(
    payload: ProjectTeamBasePayload & { teamId: number },
): Promise<RequestServiceResponse<AdminTeam>> {
    const { type, projectId, teamId } = payload;
    return await RequestService.get<AdminTeam>(
        createProjectTeamsUrl(type, projectId, teamId),
    );
}

async function createProjectTeam(
    payload: CreateProjectTeamPayload,
): Promise<RequestServiceResponse<AdminTeam>> {
    const { type, projectId, name } = payload;
    return await RequestService.post<{ name: string }, AdminTeam>(
        createProjectTeamsUrl(type, projectId),
        { name },
    );
}

async function updateProjectTeam(
    payload: UpdateProjectTeamPayload,
): Promise<RequestServiceResponse<AdminTeam>> {
    const { type, projectId, teamId, name, participants } = payload;
    const body: Omit<
        UpdateProjectTeamPayload,
        'type' | 'projectId' | 'teamId'
    > = {};
    if (name !== undefined) {
        body.name = name;
    }
    if (participants !== undefined) {
        body.participants = participants;
    }
    return await RequestService.patch<
        Omit<UpdateProjectTeamPayload, 'type' | 'projectId' | 'teamId'>,
        AdminTeam
    >(createProjectTeamsUrl(type, projectId, teamId), body, {
        allowMultiRequest: true,
    });
}

async function deleteProjectTeam(
    payload: ProjectTeamBasePayload & { teamId: number },
): Promise<RequestServiceResponse<unknown>> {
    const { type, projectId, teamId } = payload;
    return await RequestService.delete(
        createProjectTeamsUrl(type, projectId, teamId),
    );
}

async function copyProjectTeamToCompany(
    payload: CopyProjectTeamToCompanyPayload,
): Promise<RequestServiceResponse<unknown>> {
    const { type, projectId, teamId } = payload;
    return await RequestService.post<unknown>(
        `${createProjectTeamsUrl(type, projectId, teamId)}/copy-to-company`,
    );
}

async function addTeamParticipant(
    payload: AddTeamParticipantPayload,
): Promise<RequestServiceResponse<AdminTeamParticipant>> {
    const { teamId, participantId, color, isManager } = payload;
    const body: Omit<AddTeamParticipantPayload, 'teamId'> = { participantId };
    if (color !== undefined) {
        body.color = color;
    }
    if (isManager !== undefined) {
        body.isManager = isManager;
    }
    return await RequestService.post<
        Omit<AddTeamParticipantPayload, 'teamId'>,
        AdminTeamParticipant
    >(`${BASE_URL}/${teamId}/participants`, body);
}

async function addProjectTeamParticipant(
    payload: AddProjectTeamParticipantPayload,
): Promise<RequestServiceResponse<AdminTeamParticipant>> {
    const { type, projectId, teamId, participantId, color, isManager } =
        payload;
    const body: Omit<
        AddProjectTeamParticipantPayload,
        'type' | 'projectId' | 'teamId'
    > = { participantId };
    if (color !== undefined) {
        body.color = color;
    }
    if (isManager !== undefined) {
        body.isManager = isManager;
    }
    return await RequestService.post<
        Omit<AddProjectTeamParticipantPayload, 'type' | 'projectId' | 'teamId'>,
        AdminTeamParticipant
    >(`${createProjectTeamsUrl(type, projectId, teamId)}/participants`, body);
}

async function getTeamParticipants(
    payload: GetTeamParticipantsPayload,
): Promise<RequestServiceResponse<AdminTeamParticipant[]>> {
    const { teamId } = payload;
    return await RequestService.get<AdminTeamParticipant[]>(
        `${BASE_URL}/${teamId}/participants`,
    );
}

async function getTeamParticipant(
    payload: GetTeamParticipantPayload,
): Promise<RequestServiceResponse<AdminTeamParticipant>> {
    const { teamId, participantId } = payload;
    return await RequestService.get<AdminTeamParticipant>(
        `${BASE_URL}/${teamId}/participants/${participantId}`,
    );
}

async function updateTeamParticipant(
    payload: UpdateTeamParticipantPayload,
): Promise<RequestServiceResponse<AdminTeamParticipant>> {
    const { teamId, participantId, color, isManager } = payload;
    const body: Omit<UpdateTeamParticipantPayload, 'teamId' | 'participantId'> =
        {};
    if (color !== undefined) {
        body.color = color;
    }
    if (isManager !== undefined) {
        body.isManager = isManager;
    }
    return await RequestService.patch<
        Omit<UpdateTeamParticipantPayload, 'teamId' | 'participantId'>,
        AdminTeamParticipant
    >(`${BASE_URL}/${teamId}/participants/${participantId}`, body);
}

async function deleteTeamParticipant(
    payload: DeleteTeamParticipantPayload,
): Promise<RequestServiceResponse> {
    const { teamId, participantId } = payload;
    return await RequestService.delete(
        `${BASE_URL}/${teamId}/participants/${participantId}`,
    );
}

async function getProjectTeamParticipants(
    payload: GetProjectTeamParticipantsPayload,
): Promise<RequestServiceResponse<AdminTeamParticipant[]>> {
    const { type, projectId, teamId } = payload;
    return await RequestService.get<AdminTeamParticipant[]>(
        `${createProjectTeamsUrl(type, projectId, teamId)}/participants`,
    );
}

async function getProjectTeamParticipant(
    payload: GetProjectTeamParticipantPayload,
): Promise<RequestServiceResponse<AdminTeamParticipant>> {
    const { type, projectId, teamId, participantId } = payload;
    return await RequestService.get<AdminTeamParticipant>(
        `${createProjectTeamsUrl(type, projectId, teamId)}/participants/${participantId}`,
    );
}

async function updateProjectTeamParticipant(
    payload: UpdateProjectTeamParticipantPayload,
): Promise<RequestServiceResponse<AdminTeamParticipant>> {
    const { type, projectId, teamId, participantId, color, isManager } =
        payload;
    const body: Omit<
        UpdateProjectTeamParticipantPayload,
        'type' | 'projectId' | 'teamId' | 'participantId'
    > = {};
    if (color !== undefined) {
        body.color = color;
    }
    if (isManager !== undefined) {
        body.isManager = isManager;
    }
    return await RequestService.patch<
        Omit<
            UpdateProjectTeamParticipantPayload,
            'type' | 'projectId' | 'teamId' | 'participantId'
        >,
        AdminTeamParticipant
    >(
        `${createProjectTeamsUrl(type, projectId, teamId)}/participants/${participantId}`,
        body,
    );
}

async function deleteProjectTeamParticipant(
    payload: DeleteProjectTeamParticipantPayload,
): Promise<RequestServiceResponse> {
    const { type, projectId, teamId, participantId } = payload;
    return await RequestService.delete(
        `${createProjectTeamsUrl(type, projectId, teamId)}/participants/${participantId}`,
    );
}

async function getTeamReports(
    payload: GetTeamReportsPayload,
): Promise<RequestServiceResponse<Report[]>> {
    const { teamId, page = 1, limit = 10 } = payload;
    return await RequestService.get<Report[]>(`${BASE_URL}/${teamId}/reports`, {
        limit,
        page,
    });
}

async function getTeamReportsByType(
    payload: GetTeamReportsByTypePayload,
): Promise<RequestServiceResponse<Report[]>> {
    const { teamId, reportType, page = 1, limit = 10 } = payload;
    return await RequestService.get<Report[]>(
        `${BASE_URL}/${teamId}/reports/${reportType}`,
        {
            limit,
            page,
        },
    );
}

async function getTeamReport(
    payload: GetTeamReportPayload,
): Promise<RequestServiceResponse<Report>> {
    const { teamId, reportId } = payload;
    return await RequestService.get<Report>(
        `${BASE_URL}/${teamId}/reports/${reportId}`,
    );
}

async function getTeamReportResult(
    payload: GetTeamReportResultPayload,
): Promise<RequestServiceResponse<ReportResult>> {
    const { teamId, reportId, resultId } = payload;
    return await RequestService.get<ReportResult>(
        `${BASE_URL}/${teamId}/reports/${reportId}/results/${resultId}`,
    );
}

async function getProjectTeamReports(
    payload: GetProjectTeamReportsPayload,
): Promise<RequestServiceResponse<Report[]>> {
    const { type, projectId, teamId, page = 1, limit = 10 } = payload;
    return await RequestService.get<Report[]>(
        `${createProjectTeamsUrl(type, projectId, teamId)}/reports`,
        { limit, page },
    );
}

async function getProjectTeamReport(
    payload: GetProjectTeamReportPayload,
): Promise<RequestServiceResponse<Report>> {
    const { type, projectId, teamId, reportId } = payload;
    return await RequestService.get<Report>(
        `${createProjectTeamsUrl(type, projectId, teamId)}/reports/${reportId}`,
    );
}

async function getProjectTeamReportResult(
    payload: GetProjectTeamReportResultPayload,
): Promise<RequestServiceResponse<ReportResult>> {
    const { type, projectId, teamId, reportId, resultId } = payload;
    return await RequestService.get<ReportResult>(
        `${createProjectTeamsUrl(type, projectId, teamId)}/reports/${reportId}/results/${resultId}`,
    );
}

export default AdminTeamService;
